import React, { useRef, useState } from "react";
import { Upload, X, ArrowLeft, ArrowRight, Star } from "lucide-react";
import { toast } from "sonner";
import * as api from "../api";

/**
 * Shared operator gallery with reorderable images (list order == public order; the first
 * image is the cover). Upload files or paste a URL. Order is changed with left/right
 * controls (robust + testable). Works for any editor: experiences, accommodations,
 * restaurant, products.
 */
export const ReorderableGallery = ({ value = [], onChange, testid = "gallery", label = "Galleria (la 1ª foto è la copertina)" }) => {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const imgs = value || [];
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= imgs.length) return;
    const next = [...imgs];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const remove = (i) => onChange(imgs.filter((_, k) => k !== i));
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) { const r = await api.uploadMedia(f); urls.push(r.url); }
      onChange([...imgs, ...urls]);
      toast.success("Immagini caricate");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload fallito"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div data-testid={`${testid}-wrap`}>
      <span className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</span>
      <div className="flex flex-wrap gap-2 mt-2">
        {imgs.map((u, i) => (
          <div key={`${u}-${i}`} data-testid={`${testid}-item-${i}`} className="relative h-20 w-24 rounded-lg overflow-hidden border border-slate-200 group">
            <img src={u} alt="" className="h-full w-full object-cover" />
            {i === 0 && <span className="absolute top-0.5 left-0.5 bg-primary text-white rounded px-1 py-0.5 text-[9px] font-bold flex items-center gap-0.5"><Star className="h-2.5 w-2.5" /> Cover</span>}
            <button type="button" onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`} className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5"><X className="h-3 w-3" /></button>
            <div className="absolute bottom-0 inset-x-0 flex justify-between bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
              <button type="button" onClick={() => move(i, -1)} data-testid={`${testid}-left-${i}`} disabled={i === 0} className="text-white p-1 disabled:opacity-30"><ArrowLeft className="h-3 w-3" /></button>
              <button type="button" onClick={() => move(i, 1)} data-testid={`${testid}-right-${i}`} disabled={i === imgs.length - 1} className="text-white p-1 disabled:opacity-30"><ArrowRight className="h-3 w-3" /></button>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => fileRef.current.click()} data-testid={`${testid}-upload`} className="h-20 w-24 rounded-lg border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 text-xs">
          {busy ? "..." : <><Upload className="h-4 w-4 mb-1" /> Carica</>}
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} />
      </div>
      <input className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-secondary"
        placeholder="oppure incolla URL immagine e premi Invio"
        onKeyDown={(e) => { if (e.key === "Enter" && e.target.value) { onChange([...imgs, e.target.value]); e.target.value = ""; } }} />
    </div>
  );
};
